// src/pages/AdminDashboard.tsx
import { useMemo, useState, useEffect } from "react";
import { addDays, format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FilterCountBadge,
  PeriodFilterBar,
  filterTriggerClass,
  usePeriodFilter,
} from "@/components/PeriodFilter";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { getDashboardData, updateWeeklyTimesheet } from "@/api/timesheet";
import {
  downloadFilteredTimesheets,
  toLocalWeekDate,
} from "@/utils/exportTimesheets";
import {
  Download,
  Loader2,
  Edit2,
  Check,
  X as Cancel,
  Users,
  CircleDot,
  ChevronsUpDown,
} from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import AdminLeaveTab from "./AdminLeaveTab";

interface TimesheetEntry {
  id?: string;
  user_name: string;
  user_email: string;
  WeekStartDate: string;
  Monday: number;
  Tuesday: number;
  Wednesday: number;
  Thursday: number;
  Friday: number;
  Saturday: number;
  Sunday: number;
  total_hours: number;
  status: string;
  CreatedAt: string;
}

interface DashboardStats {
  totalEmployees: number;
  submittedTimesheets: number;
  submittedHours: number;
  draftHours: number;
  pendingSubmissions: number;
}

const DAY_KEYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

type DayKey = (typeof DAY_KEYS)[number];

/** A week clipped to the selected period: out-of-period days count as 0. */
interface PeriodRow extends TimesheetEntry {
  source: TimesheetEntry;
  inPeriod: boolean[];
}

const buildStats = (data: TimesheetEntry[]): DashboardStats => {
  const uniqueEmployees = new Map<string, string>();
  data.forEach((entry) => uniqueEmployees.set(entry.user_email, entry.user_name));

  const submittedCount = data.filter(
    (entry) => entry.status === "submitted" || entry.status === "approved"
  ).length;

  const draftCount = data.filter((entry) => entry.status === "draft").length;

  const submittedHours = data
    .filter((entry) => entry.status === "submitted" || entry.status === "approved")
    .reduce((sum, entry) => sum + (entry.total_hours || 0), 0);

  const draftHours = data
    .filter((entry) => entry.status === "draft")
    .reduce((sum, entry) => sum + (entry.total_hours || 0), 0);

  return {
    totalEmployees: uniqueEmployees.size,
    submittedTimesheets: submittedCount,
    submittedHours,
    draftHours,
    pendingSubmissions: draftCount,
  };
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    case "submitted":
      return "bg-primary/10 text-primary";
    case "draft":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const AdminDashboard = () => {
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([]);
  // Start in the loading state so the empty-table message doesn't flash first.
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const period = usePeriodFilter();
  const { isDayInPeriod, periodLabel: statsPeriodLabel, hasDateRange } = period;
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [isEmployeeFilterOpen, setIsEmployeeFilterOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<TimesheetEntry>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [tab, setTab] = useState<"timesheet" | "leave">("timesheet");
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const availableStatYears = useMemo(() => {
    const years = new Set<number>();
    timesheets.forEach((entry) => {
      const weekStart = toLocalWeekDate(entry.WeekStartDate);
      years.add(weekStart.getFullYear());
      years.add(addDays(weekStart, 6).getFullYear());
    });
    return [...years];
  }, [timesheets]);

  // Period is decided per day, so a month runs from its 1st to its last day
  // even when the week containing the 1st starts in the previous month.
  // Top period scopes stats, table, filter options, and export.
  const periodScopedRows = useMemo<PeriodRow[]>(() => {
    return timesheets.flatMap((entry) => {
      const weekStart = toLocalWeekDate(entry.WeekStartDate);
      const inPeriod = DAY_KEYS.map((_, offset) => isDayInPeriod(addDays(weekStart, offset)));
      if (!inPeriod.some(Boolean)) return [];

      const row: PeriodRow = { ...entry, source: entry, inPeriod };
      if (inPeriod.every(Boolean)) return [row];

      DAY_KEYS.forEach((day, offset) => {
        if (!inPeriod[offset]) row[day] = 0;
      });
      row.total_hours = DAY_KEYS.reduce((sum, day) => sum + (Number(row[day]) || 0), 0);
      return [row];
    });
  }, [timesheets, isDayInPeriod]);

  const availableEmployees = useMemo(() => {
    const employeeMap = new Map<string, string>();
    periodScopedRows.forEach((entry) => {
      if (!employeeMap.has(entry.user_email)) {
        employeeMap.set(entry.user_email, entry.user_name);
      }
    });
    return [...employeeMap.entries()]
      .map(([email, name]) => ({ email, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [periodScopedRows]);

  const availableStatuses = useMemo(() => {
    return [...new Set(periodScopedRows.map((entry) => entry.status).filter(Boolean))].sort();
  }, [periodScopedRows]);

  // Drop selections that fall outside the active period.
  useEffect(() => {
    const employeeSet = new Set(availableEmployees.map((employee) => employee.email));
    const statusSet = new Set(availableStatuses);

    setSelectedEmployees((prev) => {
      const next = prev.filter((email) => employeeSet.has(email));
      return next.length === prev.length ? prev : next;
    });
    setSelectedStatuses((prev) => {
      const next = prev.filter((status) => statusSet.has(status));
      return next.length === prev.length ? prev : next;
    });
  }, [availableEmployees, availableStatuses]);

  const stats = useMemo(() => buildStats(periodScopedRows), [periodScopedRows]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await getDashboardData();
      const rows = (data || []) as TimesheetEntry[];
      setTimesheets(rows);
      setLastUpdated(new Date());
    } catch (error) {
      // Keep whatever was already loaded; just tell the user the refresh failed.
      console.error("Error fetching data:", error);
      toast({
        variant: "destructive",
        title: "Could not load timesheets",
        description: "Please check your connection and try refreshing.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTimesheets = useMemo(() => {
    return periodScopedRows
      .filter((entry) => {
        const matchesEmployee =
          selectedEmployees.length === 0 || selectedEmployees.includes(entry.user_email);
        const matchesStatus =
          selectedStatuses.length === 0 || selectedStatuses.includes(entry.status);
        return matchesEmployee && matchesStatus;
      })
      .sort(
        (a, b) =>
          toLocalWeekDate(b.WeekStartDate).getTime() - toLocalWeekDate(a.WeekStartDate).getTime()
      );
  }, [periodScopedRows, selectedEmployees, selectedStatuses]);

  const hasActiveFilters = selectedEmployees.length > 0 || selectedStatuses.length > 0;
  // First load shows skeletons; later refreshes dim the existing data instead.
  const isInitialLoad = isLoading && lastUpdated === null;

  const handleDownload = async (formatType: "excel" | "csv" = "excel") => {
    if (filteredTimesheets.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to export",
        description: "No timesheet rows match the current filters.",
      });
      return;
    }

    try {
      setIsDownloading(true);
      downloadFilteredTimesheets(
        filteredTimesheets,
        formatType,
        {
          employees: selectedEmployees.length,
          dateRange: hasDateRange,
          statuses: selectedStatuses.length,
        },
        isDayInPeriod
      );
    } catch (error) {
      console.error("Error downloading report:", error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description:
          error instanceof Error
            ? error.message
            : "Could not generate the report. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleEmployee = (email: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(email) ? prev.filter((item) => item !== email) : [...prev, email]
    );
  };

  const handleStatusSelection = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedStatuses((prev) => [...prev, status]);
    } else {
      setSelectedStatuses((prev) => prev.filter((item) => item !== status));
    }
  };

  const clearAllFilters = () => {
    setSelectedEmployees([]);
    setSelectedStatuses([]);
  };

  const handleEdit = (entry: TimesheetEntry) => {
    const entryId = entry.id || `${entry.user_email}-${entry.WeekStartDate}`;
    setEditingId(entryId);
    setEditForm({
      Monday: entry.Monday,
      Tuesday: entry.Tuesday,
      Wednesday: entry.Wednesday,
      Thursday: entry.Thursday,
      Friday: entry.Friday,
      Saturday: entry.Saturday,
      Sunday: entry.Sunday,
      status: entry.status,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleFormChange = (field: keyof TimesheetEntry, value: string) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: field === "status" ? value : parseFloat(value) || 0,
    }));
  };

  const handleSaveEdit = async (entry: TimesheetEntry) => {
    try {
      setIsUpdating(true);

      const totalHours =
        (editForm.Monday || 0) +
        (editForm.Tuesday || 0) +
        (editForm.Wednesday || 0) +
        (editForm.Thursday || 0) +
        (editForm.Friday || 0) +
        (editForm.Saturday || 0) +
        (editForm.Sunday || 0);

      const updateData = {
        Monday: editForm.Monday || 0,
        Tuesday: editForm.Tuesday || 0,
        Wednesday: editForm.Wednesday || 0,
        Thursday: editForm.Thursday || 0,
        Friday: editForm.Friday || 0,
        Saturday: editForm.Saturday || 0,
        Sunday: editForm.Sunday || 0,
        total_hours: totalHours,
        status: editForm.status || "draft",
      };

      await updateWeeklyTimesheet(entry.WeekStartDate, {
        ...updateData,
        user_email: entry.user_email,
      });

      const updatedTimesheets = timesheets.map((item) => {
        if (item.user_email === entry.user_email && item.WeekStartDate === entry.WeekStartDate) {
          return { ...item, ...updateData };
        }
        return item;
      });
      setTimesheets(updatedTimesheets);

      setEditingId(null);
      setEditForm({});

      toast({
        title: "Success",
        description: "Timesheet updated successfully",
      });
    } catch (error) {
      console.error("Error updating timesheet:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update timesheet. Please try again.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Admin Dashboard</h1>
      <div className="mb-4 flex space-x-4 border-b">
        <button
          className={`px-4 py-2 ${tab === "timesheet" ? "border-b-2 border-primary font-bold" : ""}`}
          onClick={() => setTab("timesheet")}
        >
          Timesheet
        </button>
        <button
          className={`px-4 py-2 ${tab === "leave" ? "border-b-2 border-primary font-bold" : ""}`}
          onClick={() => setTab("leave")}
        >
          Leave
        </button>
      </div>
      <div>
        {tab === "timesheet" && (
          <>
            <PeriodFilterBar
              period={period}
              availableYears={availableStatYears}
              onRefresh={fetchData}
              isRefreshing={isLoading}
              lastUpdated={lastUpdated}
            />

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-5">
              {[
                { label: "Total Employees", value: stats.totalEmployees },
                { label: "Submitted Timesheets", value: stats.submittedTimesheets },
                { label: "Draft Submissions", value: stats.pendingSubmissions },
                { label: "Submitted Hours", value: `${stats.submittedHours}h` },
                { label: "Draft Hours", value: `${stats.draftHours}h` },
              ].map((card) => (
                <Card key={card.label} className="p-4 transition-shadow hover:shadow-md">
                  <div className="text-sm text-gray-500">{card.label}</div>
                  {isInitialLoad ? (
                    <Skeleton className="mt-1 h-8 w-16" />
                  ) : (
                    <div
                      className={cn(
                        "text-2xl font-bold transition-opacity",
                        isLoading && "opacity-50"
                      )}
                    >
                      {card.value}
                    </div>
                  )}
                </Card>
              ))}
            </div>

            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-foreground">Timesheet Dashboard</h1>
                <p className="mt-1 text-base text-muted-foreground">
                  Showing {statsPeriodLabel} · {filteredTimesheets.length} row
                  {filteredTimesheets.length === 1 ? "" : "s"}
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 rounded-2xl border border-border/70 bg-muted/30 p-2 lg:max-w-[70%]">
                <Popover open={isEmployeeFilterOpen} onOpenChange={setIsEmployeeFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={filterTriggerClass}>
                      <Users className="h-4 w-4" />
                      <span>Employees</span>
                      <FilterCountBadge count={selectedEmployees.length} />
                      <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="end">
                    <Command>
                      <CommandInput placeholder="Search employees..." />
                      <CommandList>
                        <CommandEmpty>No employees found.</CommandEmpty>
                        <CommandGroup>
                          {availableEmployees.map((employee) => {
                            const checked = selectedEmployees.includes(employee.email);
                            return (
                              <CommandItem
                                key={employee.email}
                                value={`${employee.name} ${employee.email}`}
                                onSelect={() => toggleEmployee(employee.email)}
                                className="gap-2"
                              >
                                <Checkbox
                                  checked={checked}
                                  className="pointer-events-none"
                                  aria-hidden
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-medium">{employee.name}</div>
                                  <div className="truncate text-xs text-muted-foreground">
                                    {employee.email}
                                  </div>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                    <div className="flex items-center justify-between border-t p-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setSelectedEmployees(availableEmployees.map((employee) => employee.email))
                        }
                      >
                        Select all
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedEmployees([])}
                      >
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>

                <Popover open={isStatusFilterOpen} onOpenChange={setIsStatusFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={filterTriggerClass}>
                      <CircleDot className="h-4 w-4" />
                      <span>Status</span>
                      <FilterCountBadge count={selectedStatuses.length} />
                      <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72" align="end">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Select Status</h4>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedStatuses([...availableStatuses])}
                          >
                            Select All
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedStatuses([])}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>

                      <div className="max-h-60 space-y-2 overflow-y-auto">
                        {availableStatuses.map((status) => (
                          <div key={status} className="flex items-center space-x-2">
                            <Checkbox
                              id={`status-${status}`}
                              checked={selectedStatuses.includes(status)}
                              onCheckedChange={(checked) =>
                                handleStatusSelection(status, checked as boolean)
                              }
                            />
                            <label htmlFor={`status-${status}`} className="cursor-pointer">
                              <span
                                className={cn(
                                  "rounded-full px-2 py-1 text-xs font-medium",
                                  statusBadgeClass(status)
                                )}
                              >
                                {status}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="h-10 rounded-xl text-muted-foreground"
                  >
                    Clear all
                  </Button>
                )}

                <button
                  onClick={() => handleDownload("excel")}
                  disabled={isDownloading || filteredTimesheets.length === 0}
                  className="flex h-10 items-center rounded-xl bg-primary px-4 text-white hover:bg-primary/90 disabled:opacity-50"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Export Report
                      {hasActiveFilters && (
                        <span className="ml-1 rounded bg-white/20 px-1 text-xs">
                          ({filteredTimesheets.length})
                        </span>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>

            <Card
              className={cn("p-6 transition-opacity", isLoading && !isInitialLoad && "opacity-60")}
              aria-busy={isLoading}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Week Start</TableHead>
                    <TableHead>Mon</TableHead>
                    <TableHead>Tue</TableHead>
                    <TableHead>Wed</TableHead>
                    <TableHead>Thu</TableHead>
                    <TableHead>Fri</TableHead>
                    <TableHead>Sat</TableHead>
                    <TableHead>Sun</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTimesheets.map((entry, index) => {
                    const entryId = entry.id || `${entry.user_email}-${entry.WeekStartDate}`;
                    const isEditing = editingId === entryId;

                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{entry.user_name}</div>
                            <div className="text-sm text-gray-500">{entry.user_email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {format(toLocalWeekDate(entry.WeekStartDate), "MMM dd, yyyy")}
                        </TableCell>

                        {DAY_KEYS.map(
                          (day: DayKey, offset) => (
                            <TableCell key={day}>
                              {isEditing ? (
                                <Input
                                  type="number"
                                  min="0"
                                  max="24"
                                  step="0.5"
                                  value={editForm[day as keyof TimesheetEntry] || 0}
                                  onChange={(e) =>
                                    handleFormChange(day as keyof TimesheetEntry, e.target.value)
                                  }
                                  className={cn(
                                    "h-8 w-16",
                                    "border-input bg-card text-card-foreground",
                                    "dark:border-input dark:bg-card dark:text-card-foreground",
                                    "focus:border-input focus:ring-ring"
                                  )}
                                  style={{
                                    colorScheme: "dark light",
                                  }}
                                />
                              ) : entry.inPeriod[offset] ? (
                                entry[day] || "-"
                              ) : (
                                <span
                                  className="text-muted-foreground/50"
                                  title="Outside the selected period — not counted"
                                >
                                  {entry.source[day] || "-"}
                                </span>
                              )}
                            </TableCell>
                          )
                        )}

                        <TableCell>
                          {isEditing
                            ? `${
                                (editForm.Monday || 0) +
                                (editForm.Tuesday || 0) +
                                (editForm.Wednesday || 0) +
                                (editForm.Thursday || 0) +
                                (editForm.Friday || 0) +
                                (editForm.Saturday || 0) +
                                (editForm.Sunday || 0)
                              }h`
                            : `${entry.total_hours}h`}
                        </TableCell>

                        <TableCell>
                          {isEditing ? (
                            <Select
                              value={editForm.status}
                              onValueChange={(value) => handleFormChange("status", value)}
                            >
                              <SelectTrigger className="h-8 w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">draft</SelectItem>
                                <SelectItem value="submitted">submitted</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span
                              className={cn(
                                "rounded-full px-2 py-1 text-xs font-medium",
                                statusBadgeClass(entry.status)
                              )}
                            >
                              {entry.status}
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          {isEditing ? (
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSaveEdit(entry)}
                                disabled={isUpdating}
                                className="h-8 w-8 p-0"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Check className="h-3 w-3 text-green-600" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                                className="h-8 w-8 p-0"
                              >
                                <Cancel className="h-3 w-3 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(entry.source)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {isInitialLoad && (
                    <TableRow>
                      <TableCell colSpan={12} className="py-10 text-center text-muted-foreground">
                        <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-primary" />
                        Loading timesheets…
                      </TableCell>
                    </TableRow>
                  )}

                  {filteredTimesheets.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={12} className="py-8 text-center text-gray-500">
                        No timesheet entries found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
        {tab === "leave" && <AdminLeaveTab />}
      </div>
    </div>
  );
};

export default AdminDashboard;
